import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isBefore from 'date-fns/isBefore';
import parseISO from 'date-fns/parseISO';
import differenceInDays from 'date-fns/differenceInDays';
import history from 'routing/history';
import Button, { BackButton } from 'components/Button';
import { fetchContracts } from 'products/nomad_insurance/state/contract';
import TravelIcon from 'products/nomad_insurance/assets/img/claims/travel-turquoise.svg';
import MedicalIcon from 'products/nomad_insurance/assets/img/claims/medical-turquoise.svg';
import { PAYMENT_STATUS } from 'products/nomad_insurance/constants';
import Feedback from 'products/nomad_insurance/components/Feedback';
import ViewAnimator from '../components/ViewAnimator';
import useNavigateToNextStep from '../hooks/useNavigateToNextStep';
import { setClaimant, setClaimType, editClaim } from '../state';
import { CLAIM_TYPES } from '../consts';
import {
  ClaimantSelect,
  ClaimTypeButtons,
  ClaimTypeButtonHeading,
  FeedbackWrapper
} from './styled';
import { Question, QuestionHeading, Description, NavigationButtons } from '../styled';

const ChooseClaimType = () => {
  const dispatch = useDispatch();
  const { claimant, claimType, claimID, editError, isLoading, fetchClaimError } =
    useSelector((state) => state.nomadInsurance.claims);
  const { allPaidContracts } = useSelector((state) => state.nomadInsurance.contract);
  const { user } = useSelector((state) => state.app);
  useNavigateToNextStep(
    `/nomad-insurance/claim/${claimType}/travel-information`,
    editError
  );

  const today = new Date();
  const isContractClaimable = (c) => {
    if (isBefore(today, parseISO(c.startDate))) {
      return false;
    }
    if (differenceInDays(parseISO(c.endDate), today) > 90) {
      return false;
    }
    return true;
  };

  const possibleClaimants =
    allPaidContracts
      ?.filter((c) => c.paymentStatus === PAYMENT_STATUS.ACTIVE || isContractClaimable(c))
      .flatMap((c) =>
        c.groupMemberContracts.map((memberContract) => {
          const member = `${memberContract.groupMember.firstName} ${memberContract.groupMember.lastName}`;
          return {
            value: memberContract.groupMemberID,
            label: member
          };
        })
      ) || [];

  const claimantSelectOptions = useMemo(
    () => [{ value: user.userID, label: 'Myself' }, ...possibleClaimants],
    [user, possibleClaimants]
  );

  useEffect(() => {
    if (!allPaidContracts) {
      dispatch(fetchContracts());
    }
  }, [dispatch, allPaidContracts]);

  useEffect(() => {
    if (!claimant) {
      dispatch(setClaimant(user.userID));
    }
  }, [dispatch, claimant, user]);

  return (
    <ViewAnimator>
      {fetchClaimError && (
        <FeedbackWrapper>
          <Feedback variant="failure">
            Something went wrong with fetching your claim. Please create a new claim or
            contact our customer support.
          </Feedback>
        </FeedbackWrapper>
      )}
      {possibleClaimants?.length > 0 && (
        <Question>
          <QuestionHeading>Who are you filing for?</QuestionHeading>
          <Description>
            If the event happened to you and your members, choose ‘myself’ below and you
            can tell us about your group more in the next step.
          </Description>
          <ClaimantSelect
            options={claimantSelectOptions}
            onChange={(selected) => dispatch(setClaimant(selected.value))}
            value={claimant}
            variant="outline"
          />
        </Question>
      )}
      <Question>
        <QuestionHeading>What benefits are you claiming for?</QuestionHeading>
        <ClaimTypeButtons>
          <Button
            variant="flat"
            $selected={claimType === CLAIM_TYPES.MEDICAL}
            onClick={() => dispatch(setClaimType(CLAIM_TYPES.MEDICAL))}
          >
            <ClaimTypeButtonHeading>
              <img src={MedicalIcon} alt="medical" />
              <b>Medical</b>
            </ClaimTypeButtonHeading>
            Such as doctors, hospitals, prescription medicine.
          </Button>
          <Button
            variant="flat"
            $selected={claimType === CLAIM_TYPES.TRAVEL}
            onClick={() => dispatch(setClaimType(CLAIM_TYPES.TRAVEL))}
          >
            <ClaimTypeButtonHeading>
              <img src={TravelIcon} alt="travel" />
              <b>Non-medical</b>
            </ClaimTypeButtonHeading>
            Such as canceled flights, stolen electronics, natural disaster evacuation.
          </Button>
        </ClaimTypeButtons>
      </Question>
      <NavigationButtons>
        <BackButton
          variant="dark"
          onClick={() => history.push('/nomad-insurance/profile/claims')}
        >
          Back
        </BackButton>
        <Button
          onClick={async () => {
            if (claimID) {
              await dispatch(editClaim({ claimID }));
            } else {
              history.push(`/nomad-insurance/claim/${claimType}/travel-information`);
            }
          }}
          disabled={!claimType || isLoading}
        >
          Continue
        </Button>
      </NavigationButtons>
    </ViewAnimator>
  );
};

export default ChooseClaimType;
